// src/theme/typography.ts
//
// The single source of typographic truth, in the same spirit as `./brand` for
// colour. Every font stack in the product — the MUI theme, the ECharts and
// Plotly themes, the Monaco editor, chart tooltips built as HTML strings, the
// share-card renderer — resolves back to one of the four stacks below. Nothing
// else may spell a family name out by hand.
//
// ─── Why these are stacks and not @font-face rules ───────────────────────────
//
// San Francisco cannot be self-hosted. Apple's licence covers use on Apple
// platforms only; there is no webfont build and no CDN copy that is legal to
// serve from our origin. The supported way to reach it from a browser is the
// system keyword, which is why every stack starts with `-apple-system` /
// `BlinkMacSystemFont` / `ui-*` rather than a font file we ship.
//
// That keyword is also strictly better than naming the family: it hands the
// text to Core Text, which picks the right OPTICAL SIZE automatically — SF Pro
// Text below 20px, SF Pro Display above it, with the tighter tracking and the
// smaller apertures each of those is drawn for. A stack that said
// `'SF Pro Display'` outright would get Display at 11px, which is precisely the
// mistake the optical sizes exist to prevent. The named `'SF Pro Text'` /
// `'SF Pro Display'` entries sit AFTER the keywords, where they are only ever
// reached on a non-Apple machine that has the developer download installed.
//
// ─── Why Inter and JetBrains Mono are still bundled ──────────────────────────
//
// They are no longer the primary faces, but deleting them would undo the reason
// they were vendored in the first place: SF exists on roughly the Apple half of
// the audience, and the other half would fall through to Segoe UI on Windows
// and Roboto on Linux/Android — three identities again, which is the exact
// failure the self-hosting was adopted to fix. So the order is: native SF where
// it exists, our own bundled face everywhere else, and the OS faces below that
// as a last resort if the bundle itself fails to load.
//
// Inter is the right partner for this specifically because it is metrically
// close to SF Pro Text — x-height, cap height and average advance all land
// within a couple of percent — so the layout does not reflow across the
// boundary. Same for JetBrains Mono against SF Mono: both are drawn on a 0.6em
// advance, so a column of figures occupies identical width in either face.
// (That equality is what lets `components/data/grid/gridModel.ts` keep a single
// `CHAR_W.mono` constant for both.)
//
// ─── SF Compact: only where width is the binding constraint ──────────────────
//
// SF Compact is not "SF, but smaller". It is a separate cut with flattened
// sides and narrower bowls, drawn for surfaces where the horizontal budget runs
// out before the type size does — the watchOS case Apple designed it for. Used
// anywhere else it simply looks like a condensed knock-off of the real face,
// and mixing the two in one view is visible.
//
// So the rule here is: reach for {@link COMPACT} only when a label cannot wrap,
// cannot truncate acceptably, and has a fixed share of a small viewport. At the
// time of writing exactly one surface qualifies — the mobile bottom navigation,
// where five `white-space: nowrap` labels (up to "Fintelligent" and
// "Organization") split a 375px viewport. Everything else uses {@link SANS}.

/**
 * The UI face. SF Pro on Apple platforms, bundled Inter everywhere else.
 *
 * This is the default for all body copy, labels, headings and controls — it is
 * what `theme.typography.fontFamily` is set to, so components inherit it and
 * almost nothing should need to name it explicitly.
 */
export const SANS =
  "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', " +
  "'Inter Variable', Inter, system-ui, 'Segoe UI', Roboto, Helvetica, Arial, " +
  'sans-serif';

/**
 * The monospace face, for figures, tickers, identifiers and code. SF Mono on
 * Apple platforms, bundled JetBrains Mono everywhere else.
 *
 * `ui-monospace` is the keyword that resolves to SF Mono; `SFMono-Regular` is
 * the older hook that Chrome and pre-16.4 Safari still need on macOS.
 *
 * Prefer this over the bare `monospace` keyword even for throwaway code spans:
 * unqualified `monospace` resolves to the browser's default fixed face (Courier
 * on some configurations), which is both off-brand and 20% narrower than the
 * width estimates in the instrument grid assume.
 */
export const MONO =
  "ui-monospace, SFMono-Regular, 'SF Mono', 'JetBrains Mono Variable', " +
  "'JetBrains Mono', Menlo, Consolas, 'Liberation Mono', monospace";

/**
 * The condensed UI face, for width-bound micro-labels only — see the header for
 * the rule. Falls through to {@link SANS} wherever SF Compact is unavailable,
 * so a caller never has to guard for the non-Apple case.
 */
export const COMPACT = `'SF Compact Text', 'SF Compact Display', 'SF Compact', ${SANS}`;

/**
 * The serif display face for the investor tearsheet's headline figures —
 * deliberately distinct from the UI sans so a factsheet reads as a printed
 * document rather than as another dashboard.
 *
 * `ui-serif` resolves to New York, which is Apple's own serif companion to SF
 * and shares its metrics and optical-size behaviour; the previous Iowan Old
 * Style stack is kept below it so nothing regresses off Apple.
 */
export const SERIF =
  "ui-serif, 'New York', 'Iowan Old Style', 'Palatino Linotype', Palatino, " +
  "Georgia, 'Times New Roman', serif";

/**
 * The stacks as CSS custom properties, installed on `:root` by the theme's
 * `CssBaseline` override.
 *
 * These exist for the call sites that are raw CSS strings rather than `sx`
 * objects — ECharts `formatter` tooltips built as HTML, the screener grid's
 * `var(--font-mono, …)` cells — so those can reference the token instead of
 * re-spelling a stack that then drifts. (`--font-mono` was already being read
 * in three places before it was ever defined anywhere, which meant every one of
 * them had been silently running on its inline fallback.)
 */
export const fontCssVars = {
  '--font-sans': SANS,
  '--font-mono': MONO,
  '--font-compact': COMPACT,
  '--font-serif': SERIF,
} as const;
